/* eslint @ngrx/prefer-effect-callback-in-block-statement: off */
import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { catchError, exhaustMap, map, of, tap } from 'rxjs';

import { BillingV3Service } from '@ninety/platform/services/services/billing-v3.service';

import { appActions } from '../../../app.actions';
import { MANAGE_SEATS_DIALOG } from '../../../tokens/billing-dialog.tokens';
import { BannerNotificationActions } from '../../banner-notification/banner-notification.actions';
import { BillingStateActions } from '../../billing/billing-state.actions';
import { SpinnerActions } from '../../spinner/spinner-state.actions';

import { SubscriptionActions } from './subscription-state.actions';
import { selectBanner } from './subscription-state.selectors';

@Injectable()
export class SubscriptionStateEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly billingV3Service = inject(BillingV3Service);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);

  private readonly openManageSeats = inject(MANAGE_SEATS_DIALOG);

  setTier1Data_checkForBanners$ = createEffect(() =>
    this.actions$.pipe(
      ofType(appActions.setTier1Data),
      map(() => SubscriptionActions.checkForBanners())
    )
  );

  private openManageSeatsDialog() {
    this.openManageSeats({
      width: '600px',
      minHeight: '350px',
      autoFocus: '#standardSeatCountInput',
      disableClose: true,
    }).afterClosed();
  }

  openManageSeatsDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SubscriptionActions.openManageSeatsDialog),
        tap(() => this.openManageSeatsDialog())
      ),
    { dispatch: false }
  );

  goToBillingManageSeats$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SubscriptionActions.goToBillingManageSeats),
        tap(() => this.router.navigateByUrl('settings/billing/overview').then(() => this.openManageSeatsDialog()))
      ),
    { dispatch: false }
  );

  checkForBanners$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SubscriptionActions.checkForBanners, SubscriptionActions.getUpdatedSubscriptionSuccess),
      concatLatestFrom(() => this.store.select(selectBanner)),
      map(([_, banner]) => {
        if (banner) {
          return BannerNotificationActions.displayBanner({ banner: banner });
        } else {
          return BannerNotificationActions.noBanner();
        }
      })
    )
  );

  autoIncrementSubscriptionQuantity$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SubscriptionActions.autoIncrementSubscriptionQuantity),
      exhaustMap(({ quantity }) =>
        this.billingV3Service.updateSubscriptionQuantity(quantity).pipe(
          map(response => {
            if (!response.provider) {
              return SubscriptionActions.autoIncrementSubscriptionQuantityFailure({
                error: null,
                customMessage: 'Failed to auto increment seats',
              });
            }
            return SubscriptionActions.autoIncrementSubscriptionQuantitySuccess({ subscription: response });
          }),
          catchError((error: unknown) =>
            of(
              SubscriptionActions.autoIncrementSubscriptionQuantityFailure({
                error,
                customMessage: 'Failed to auto increment seats',
              })
            )
          )
        )
      )
    )
  );

  autoIncrementSubscriptionQuantityBillingPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SubscriptionActions.autoIncrementSubscriptionQuantityBillingPage),
      exhaustMap(({ quantity }) =>
        this.billingV3Service.updateSubscriptionQuantity(quantity).pipe(
          map(response => {
            if (!response.provider) {
              return SubscriptionActions.autoIncrementSubscriptionQuantityBillingPageFailure({
                error: null,
                customMessage: 'Failed to auto increment seats',
              });
            }
            return SubscriptionActions.autoIncrementSubscriptionQuantityBillingPageSuccess({ subscription: response });
          }),
          catchError((error: unknown) =>
            of(
              SubscriptionActions.autoIncrementSubscriptionQuantityBillingPageFailure({
                error,
                customMessage: 'Failed to auto increment seats',
              })
            )
          )
        )
      )
    )
  );

  autoIncrementSubscriptionQuantityBillingPageComplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        SubscriptionActions.autoIncrementSubscriptionQuantityBillingPageSuccess,
        SubscriptionActions.autoIncrementSubscriptionQuantityBillingPageFailure
      ),
      tap(() => this.dialog.closeAll()),
      map(() => BillingStateActions.init())
    )
  );

  subscriptionActionStart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        SubscriptionActions.getUpdatedSubscription,
        SubscriptionActions.openManageSeatsDialog,
        SubscriptionActions.autoIncrementSubscriptionQuantityBillingPage
      ),
      map(action => SpinnerActions.startPrimary({ source: action.type }))
    )
  );

  subscriptionActionEnd$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        SubscriptionActions.getUpdatedSubscriptionSuccess,
        SubscriptionActions.getUpdatedSubscriptionFailure,
        SubscriptionActions.autoIncrementSubscriptionQuantityBillingPageSuccess,
        SubscriptionActions.autoIncrementSubscriptionQuantityBillingPageFailure
      ),
      map(action => SpinnerActions.stopPrimary({ source: action.type }))
    )
  );
}
